<template>
  <div class="banner">
    <div class="header">
      <img class="logo" :src="LogoImg" alt="logo">
      <div
        class="lang"
        @mouseenter="onPcHover"
        @mouseleave="onPcLeave"
      >
        <button type="button" class="lang-btn" @click="toggleMobileLangList">
          <span>{{ currentLabel }}</span>
          <i class="lang-arrow" />
        </button>
        <div v-show="showLangList" class="lang-list">
          <button
            v-for="lang in langOptions"
            :key="lang.value"
            type="button"
            class="lang-item"
            :class="{ active: lang.value === locale }"
            @click="changeLang(lang.value)"
          >
            {{ lang.label }}
          </button>
        </div>
      </div>
    </div>
    <section class="stage-panel">
      <img class="stage-head" src="~@/views/act/halloween/assets/img/stage-head.png" alt="stage-head">
      <div class="stage-title">{{ t('halloween.globalTitle') }}</div>
      <!-- <div class="stage-total">
        <img class="stage-total-icon" src="~@/views/act/halloween/assets/img/nangua-small.png" alt="stage-icon">
        <span class="stage-total-num">0</span>
      </div> -->
      <div class="stage-progress">
        <div class="stage-progress-bar">
          <div class="stage-progress-bar-fill" :class="{ 'is-full': stageProgress >= 98 }" :style="{ height: `${stageProgress}%` }"></div>
          <div class="stage-progress-bar-target" :style="{ top: `${100 - stageProgress}%` }">
            <span>{{ stageProgress }}%</span>
          </div>
          <div class="line line-1"></div>
          <div class="line line-2"></div>
          <div class="line line-3"></div>
          <div class="reward-wrap">
            <div
              class="reward-item"
              :class="`reward-item-${stage.percent}`"
              v-for="stage in stages"
              :key="stage.stage"
              :disabled="stage.received"
              @click="onStageClick(stage)"
            >
              <img :class="{ received: stage.received, 'can-receive': canReceiveStage(stage) }" class="reward-item-icon" src="~@/views/act/halloween/assets/img/gift.png" alt="gift">
              <span class="reward-item-num">{{ stage.percent }}%</span>
            </div>
          </div>
        </div>
        <!-- <article v-for="stage in stages" :key="stage.stage" class="stage">
          <strong>{{ stage.percent }}%</strong>
          <span>{{ stageStatusText(stage) }}</span>
          <button
            type="button"
            class="stage-btn"
            :disabled="!canReceiveStage(stage)"
            @click="emit('receive', stage)"
          >
            {{ stageButtonText(stage) }}
          </button>
        </article> -->
      </div>
    </section>
    <div class="main-title" :class="locale">
      <img :class="locale" :src="MainTitleImg" alt="main-title">
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, defineProps, defineEmits } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { langMap, supportedLangs } from '@/config/lang'
import { isMobile } from '@/utils'
import Logo from '@/assets/imgs/logo.png'
import LogoHk from '@/assets/imgs/logo-hk.png'
import MainTitleMap from '@/views/act/halloween/assets/img/mainTitle/index.js'

const store = useStore()
const route = useRoute()
const router = useRouter()
const { t, locale } = useI18n()
const showLangList = ref(false)

const props = defineProps({
  stages: {
    type: Array,
    default: () => [],
  },
  canPlay: {
    type: Boolean,
    default: false,
  },
  // 可领全服节点（play + view_only）
  canClaim: {
    type: Boolean,
    default: false,
  },
})
const emit = defineEmits(['receive', 'preview'])

const onStageClick = (stage) => {
  if (canReceiveStage(stage)) {
    emit('receive', stage)
    return
  }
  emit('preview', stage)
}

const LogoImg = computed(() => {
  return locale.value === 'hk' || locale.value === 'tw' ? LogoHk : Logo
})
const MainTitleImg = computed(() => {
  return MainTitleMap[locale.value] || MainTitleMap.en
})
const langOptions = computed(() =>
  supportedLangs.map((value) => ({ value, label: langMap[value] || value })),
)
const currentLabel = computed(() => langMap[locale.value] || locale.value)

const canReceiveStage = (stage) => props.canClaim && stage?.unlocked && !stage?.received

const PROGRESS_TICK_INTERVAL = 60 * 1000
const nowTs = ref(Date.now())
let progressTimer = null

const parseServerTime = (time) => {
  if (!time) return null
  const value = String(time).trim()
  if (!value) return null
  const normalized = value.replace(' ', 'T')
  const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(normalized)
  const ts = Date.parse(hasTimezone ? normalized : `${normalized}+08:00`)
  return Number.isFinite(ts) ? ts : null
}

const stageProgress = computed(() => {
  const stages = (props.stages || [])
    .map((stage) => ({
      percent: Number(stage.percent) || 0,
      time: parseServerTime(stage.unlockTime),
    }))
    .filter((stage) => stage.time)
    .sort((a, b) => a.time - b.time)
  if (!stages.length) return 0

  const startTime = parseServerTime(store.state.hwAct?.commonConfig?.activityStartTime)
  const points = startTime ? [{ percent: 0, time: startTime }] : []
  stages.forEach((stage) => points.push(stage))

  const now = nowTs.value
  if (now <= points[0].time) return 0

  let value = points[points.length - 1].percent
  for (let i = 1; i < points.length; i += 1) {
    const prev = points[i - 1]
    const next = points[i]
    if (now <= next.time) {
      const duration = next.time - prev.time
      value = duration <= 0
        ? next.percent
        : prev.percent + ((next.percent - prev.percent) * (now - prev.time)) / duration
      break
    }
  }
  return Math.max(0, Math.min(100, Math.floor(value)))
})

const changeLang = (lang) => {
  showLangList.value = false
  locale.value = lang
  store.commit('SET_LOCALE', lang)
  localStorage.setItem('user_selected_locale', lang)
  if (route.params.locale !== lang) {
    router.push({
      name: route.name,
      params: { ...route.params, locale: lang },
      query: route.query,
    })
  }
}

const toggleMobileLangList = () => {
  if (isMobile) {
    showLangList.value = !showLangList.value
  }
}

const onPcHover = () => {
  if (!isMobile) {
    showLangList.value = true
  }
}

const onPcLeave = () => {
  if (!isMobile) {
    showLangList.value = false
  }
}

const closeDropdown = (event) => {
  if (!event.target.closest('.lang')) {
    showLangList.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', closeDropdown)
  progressTimer = setInterval(() => {
    nowTs.value = Date.now()
  }, PROGRESS_TICK_INTERVAL)
})

onUnmounted(() => {
  document.removeEventListener('click', closeDropdown)
  if (progressTimer) {
    clearInterval(progressTimer)
    progressTimer = null
  }
})
</script>

<style lang="less" scoped>
@keyframes gift-claim {
  0%,
  100% {
    transform: translateY(0) scale(1);
    filter: brightness(1);
  }
  35% {
    transform: translateY(-4px) scale(1.2);
    filter: brightness(1.4);
  }
  55% {
    transform: translateY(0) scale(1);
    filter: brightness(1);
  }
  70% {
    transform: translateY(-2px) scale(1.08);
  }
}
.banner {
  position: relative;
  width: 750px;
  height: 739px;
  background: url('~@/views/act/halloween/assets/img/kv.png') no-repeat center center / 100% 100%;
  .header {
    position: relative;
    height: 75px;
    .logo {
      position: absolute;
      top: 15px;
      left: 10px;
      height: 45px;
      z-index: 1;
    }
  }
}

.lang {
  position: absolute;
  top: 12px;
  right: 0;
  z-index: 1;
  width: 180px;
  color: #fff;
  font-size: 22px;
  font-weight: bold;
}

.lang-btn,
.lang-item {
  width: 100%;
  height: 46px;
  padding: 0 10px;
  border: 0;
  box-sizing: border-box;
  color: #fff;
  font-size: 22px;
  font-weight: bold;
  cursor: pointer;
}

.lang-btn {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #06255c;
}

.lang-arrow {
  position: absolute;
  top: 50%;
  right: 14px;
  width: 0;
  height: 0;
  transform: translateY(-50%);
  border-left: 7px solid transparent;
  border-right: 7px solid transparent;
  border-top: 8px solid #fff;
}

.lang-list {
  background: #4a1382;
}

.lang-item {
  background: #4a1382;

  &:hover {
    background: #5041ac;
  }

}

.stage-panel {
  width: 133px;
  height: 458px;
  background: url('~@/views/act/halloween/assets/img/stage-bg.png') no-repeat center center / 100% 100%;
  position: absolute;
  top: 116px;
  left: 20px;
  padding-top: 30px;
  display: flex;
  flex-direction: column;
  align-items: center;
  .stage-head {
    width: 137px;
    height: 80px;
    position: absolute;
    top: -55px;
    left: 0;
  }
  .stage-title {
    color: #FFF100;
    font-size: 13px;
    text-align: center;
    padding: 0 20px;
  }
  .stage-total {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    margin-top: 5px;
    .stage-total-icon {
      width: 20px;
      height: 20px;
    }
    .stage-total-num {
      color: #fff;
      font-size: 13px;
    }
  }
  .stage-progress {
    margin-top: 30px;
    flex: 1;
    .stage-progress-bar {
      height: 330px;
      width: 36px;
      border-radius: 30px;
      border: 5px solid #FFFE83;
      position: relative;
      margin-right: 40px;
      .line {
        width: 16px;
        height: 6px;
        background: #fff;
        border: 2px solid #FFFF5F;
        position: absolute;
        right: -8px;
        transform: translateY(50%);
      }
      .line-1 {
        bottom: 25%;
      }
      .line-2 {
        bottom: 50%;
      }
      .line-3 {
        bottom: 75%;
      }
      .reward-wrap {
        .reward-item {
          position: absolute;
          right: -55px;
          transform: translateY(10px);
          display: flex;
          align-items: center;
          &:disabled {
            cursor: not-allowed;
          }
          &.reward-item-25 {
            bottom: 25%;
          }
          &.reward-item-50 {
            bottom: 50%;
          }
          &.reward-item-75 {
            bottom: 75%;
          }
          &.reward-item-100 {
            bottom: 100%;
            transform: translateY(10px) translateX(10px) scale(1.1);
            .reward-item-num {
              color: #FFFF00;
            }
          }
          .reward-item-icon {
            width: 16px;
            height: 16px;
            margin-right: 2px;
            &.received {
              filter: grayscale(100%);
            }
            &.can-receive {
              transform-origin: center bottom;
              animation: gift-claim 1.2s ease-in-out infinite;
            }
          }
          .reward-item-num {
            color: #fff;
            font-size: 14px;
          }
        }
      }
      .stage-progress-bar-fill {
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        background: #F7B50A;
        border-bottom-left-radius: 13px;
        border-bottom-right-radius: 13px;
      }
      .stage-progress-bar-target {
        
        height: 20px;
        border: 2px solid #FDF85D;
        border-radius: 10px;
        font-size: 13px;
        position: absolute;
        left: -40px;
        transform: translateY(-50%);
        padding: 5px 2px;
        display: flex;
        align-items: center;
        justify-content: center;
        &::before {
          z-index: 2px;
          content: '';
          position: absolute;
          right: -10px;
          width: 8px;
          height: 8px;
          background: linear-gradient(to right, #FFA82A, #EDFF5B);
          border: 2px solid #FFFF62;
          transform: rotate(45deg);
          box-sizing: border-box;
        }
      }
    }
  }
}

.stage-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.stage {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 10px;
  background: rgba(32, 18, 44, 0.72);
  border-radius: 8px;
  font-size: 22px;
}

.stage-btn {
  border: 0;
  border-radius: 8px;
  padding: 8px 12px;
  background: #ff8a2a;
  color: #1b1028;
  font-size: 22px;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
}
.main-title {
  img{
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    bottom: 20px;
    &.hk, &.tw {
      height: 216px;
    }
    &.en, &.sg, &.my, &.ph {
      height: 155px;
    }
    &.th {
      height: 263px;
    }
    &.vi {
      height: 156px;
    }
    &.id {
      height: 155px;
    }
  }

}
.stage-progress-bar-fill.is-full {
  border-top-left-radius: 13px;
  border-top-right-radius: 13px;
}
</style>
